import { Typography } from 'antd';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { BRAND_COLORS } from '../../../../../../constants/styles';
import { METRIC_HIGHER_IS_BETTER, STATUS_COLORS } from '../../../../constants';
import { retentionHistoryConfig } from '../../../config/metricHistory';
import { IModalFilterContext, IRetentionDetailsData } from '../../../types';
import { formatActiveStatuses } from '../../../utils/formatActiveStatuses';
import { ExplainerBox } from '../../shared/ExplainerBox';
import { FormulaBox } from '../../shared/FormulaBox';
import { FormulaLine } from '../../shared/FormulaLine';
import { MetricDetailsModal } from '../../shared/MetricDetailsModal';
import { MetricHistoryChart } from '../../shared/MetricHistoryChart';
import { MetricsDivider } from '../../shared/MetricsDivider';
import { StatRow } from '../../shared/StatRow';
import { ThresholdLegend } from '../../shared/ThresholdLegend';

const { Text } = Typography;

interface IRetentionDetailsModalProps {
  visible: boolean;
  onClose: () => void;
  data: IRetentionDetailsData | null;
  filter: IModalFilterContext;
  repeatDonationsThreshold?: number;
  historyDataCutoff?: string;
}

export const RetentionDetailsModal: FC<IRetentionDetailsModalProps> = ({
  visible,
  onClose,
  data,
  filter,
  repeatDonationsThreshold,
  historyDataCutoff,
}) => {
  const { t } = useTranslation('dashboard.RecruiterDashboard.retentionModal');
  if (!data) return null;

  const firstTimeDonorCount = Math.max(
    0,
    data.activeDonorCount - data.repeatDonorCount
  );

  return (
    <MetricDetailsModal
      visible={visible}
      onClose={onClose}
      data={data}
      title={t('title')}
      tooltip={t('tooltip')}
      sectionLabel={t('sectionCurrentPeriod')}
      headerValue={`${data.retentionScore}%`}
      explainer={
        <ExplainerBox>
          <strong style={{ color: '#fff' }}>{t('explainerTerm')}</strong>{' '}
          {t('explainerDef')}
          <br />
          <Text
            style={{
              color: BRAND_COLORS.secondary.lightGray,
              fontSize: 11,
              marginTop: 4,
            }}
          >
            {t('explainerActiveStatuses', {
              statuses: formatActiveStatuses(filter.activeDonorStatuses),
            })}
          </Text>
        </ExplainerBox>
      }
      historySection={
        <MetricHistoryChart
          config={retentionHistoryConfig}
          live={data}
          filter={filter}
          extra={{ repeatDonationsThreshold, historyDataCutoff }}
          translationNs="dashboard.RecruiterDashboard.retentionModal"
        />
      }
    >
      <StatRow label={t('statTotalActive')} value={data.activeDonorCount} />
      <StatRow
        label={t('statRepeatDonors')}
        value={data.repeatDonorCount}
        color={STATUS_COLORS.good.fill}
        indent
      />
      <StatRow
        label={t('statFirstTimeDonors')}
        value={firstTimeDonorCount}
        color={STATUS_COLORS.ok.fill}
        indent
      />
      <MetricsDivider />
      <FormulaBox>
        <FormulaLine>{t('formula')}</FormulaLine>
        <FormulaLine marginTop={4}>
          = {data.repeatDonorCount} ÷ {data.activeDonorCount} × 100{' = '}
          <span style={{ color: data.statusColor, fontWeight: 700 }}>
            {data.retentionScore}%
          </span>
        </FormulaLine>
      </FormulaBox>
      <ThresholdLegend
        breakpoints={data.breakpoints}
        higherIsBetter={METRIC_HIGHER_IS_BETTER.retention}
        labels={{
          low: t('thresholdLow'),
          mid: t('thresholdMid'),
          high: t('thresholdHigh'),
        }}
      />
    </MetricDetailsModal>
  );
};
